import { 
  collection, 
  addDoc, 
  updateDoc, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  onSnapshot 
} from 'firebase/firestore';
import { db } from '../firebase';
import { notificationService } from './notificationService';

export const ticketService = {
  // Create new ticket
  async createTicket(ticketData) {
    try {
      const payload = { ...ticketData };
      // Backfill ownerId/propertyName if propertyId is provided
      try {
        if (!payload.ownerId && payload.propertyId) {
          const prop = await getDoc(doc(db, 'properties', payload.propertyId));
          if (prop.exists()) {
            const pd = prop.data();
            payload.ownerId = pd.ownerId || payload.ownerId;
            payload.propertyName = payload.propertyName || pd.name;
          }
        }
      } catch (e) {
        console.error('Failed to backfill owner from property for ticket:', e);
      }

      const docRef = await addDoc(collection(db, 'tickets'), {
        ...payload,
        createdAt: new Date(),
        updatedAt: new Date(),
        status: payload.status || 'open'
      });
      // Fire notification to owner
      try {
        if (ticketData.ownerId) {
          await notificationService.createNotification({
            recipientId: ticketData.ownerId,
            type: 'ticket_created',
            title: ticketData.title ? `New ticket: ${ticketData.title}` : 'New maintenance ticket',
            body: ticketData.description?.slice(0, 120) || 'A new ticket has been submitted.',
            resourceType: 'ticket',
            resourceId: docRef.id,
          });
        }
      } catch (e) {
        console.error('Failed to create owner notification for ticket:', e);
      }
      return docRef.id;
    } catch (error) {
      throw error;
    }
  },

  // Get ticket by ID
  async getTicket(ticketId) {
    try {
      const ticketDoc = await getDoc(doc(db, 'tickets', ticketId));
      if (ticketDoc.exists()) {
        return { id: ticketDoc.id, ...ticketDoc.data() };
      }
      return null;
    } catch (error) {
      throw error;
    }
  },

  // Update ticket
  async updateTicket(ticketId, updates) {
    try {
      const ticketRef = doc(db, 'tickets', ticketId);
      await updateDoc(ticketRef, {
        ...updates,
        updatedAt: new Date()
      });
    } catch (error) {
      throw error;
    }
  },

  // Get all tickets (Admin function)
  async getAllTickets() {
    try {
      const ticketsCollection = collection(db, 'tickets');
      const ticketsSnapshot = await getDocs(ticketsCollection);
      const tickets = [];
      
      ticketsSnapshot.forEach((doc) => {
        tickets.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      return tickets;
    } catch (error) {
      console.error('Error getting all tickets:', error);
      throw error;
    }
  },

  // Get tickets by property ID
  async getTicketsByProperty(propertyId) {
    try {
      const q = query(
        collection(db, 'tickets'),
        where('propertyId', '==', propertyId)
        // Temporarily removed orderBy to avoid index requirement
      );
      const querySnapshot = await getDocs(q);
      const tickets = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort tickets client-side for now
      tickets.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return dateB - dateA; // Descending order
      });
      return tickets;
    } catch (error) {
      throw error;
    }
  },

  // Get tickets by user role
  async getTicketsByUser(userId, userRole) {
    try {
      let q;
      if (userRole === 'owner') {
        q = query(
          collection(db, 'tickets'),
          where('ownerId', '==', userId)
          // Temporarily removed orderBy to avoid index requirement
        );
      } else if (userRole === 'tenant') {
        q = query(
          collection(db, 'tickets'),
          where('tenantId', '==', userId)
          // Temporarily removed orderBy to avoid index requirement
        );
      } else if (userRole === 'serviceProvider') {
        q = query(
          collection(db, 'tickets'),
          where('serviceProviderIds', 'array-contains', userId)
          // Temporarily removed orderBy to avoid index requirement
        );
      }
      
      const querySnapshot = await getDocs(q);
      const tickets = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort tickets client-side for now
      tickets.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return dateB - dateA; // Descending order
      });
      return tickets;
    } catch (error) {
      throw error;
    }
  },

  // Listen to ticket changes in real-time
  subscribeToTicket(ticketId, callback) {
    return onSnapshot(doc(db, 'tickets', ticketId), (doc) => {
      if (doc.exists()) {
        callback({ id: doc.id, ...doc.data() });
      } else {
        callback(null);
      }
    });
  },

  // Listen to tickets collection changes
  subscribeToTickets(userId, userRole, callback) {
    try {
      let q;
      if (userRole === 'owner') {
        q = query(
          collection(db, 'tickets'),
          where('ownerId', '==', userId)
          // Temporarily removed orderBy to avoid index requirement
        );
      } else if (userRole === 'tenant') {
        q = query(
          collection(db, 'tickets'),
          where('tenantId', '==', userId)
          // Temporarily removed orderBy to avoid index requirement
        );
      } else if (userRole === 'serviceProvider') {
        q = query(
          collection(db, 'tickets'),
          where('serviceProviderIds', 'array-contains', userId)
          // Temporarily removed orderBy to avoid index requirement
        );
      } else {
        // Default case - return empty array
        callback([]);
        return () => {};
      }

      return onSnapshot(q, (querySnapshot) => {
        const tickets = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        // Sort tickets client-side for now
        tickets.sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
          const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
          return dateB - dateA; // Descending order
        });
        callback(tickets);
      }, (error) => {
        console.error('Error in ticket subscription:', error);
        callback([]);
      });
    } catch (error) {
      console.error('Error setting up ticket subscription:', error);
      callback([]);
      return () => {};
    }
  },

  // Subscribe to tickets by property
  subscribeToTicketsByProperty(propertyId, callback) {
    try {
      const q = query(
        collection(db, 'tickets'),
        where('propertyId', '==', propertyId)
      );

      return onSnapshot(q, (querySnapshot) => {
        const tickets = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        // Sort tickets client-side for now
        tickets.sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
          const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
          return dateB - dateA; // Descending order
        });
        callback(tickets);
      }, (error) => {
        console.error('Error in ticket subscription by property:', error);
        callback([]);
      });
    } catch (error) {
      console.error('Error setting up ticket subscription by property:', error);
      callback([]);
      return () => {};
    }
  },

  // Subscribe to open tickets for service providers
  subscribeToOpenTickets(callback) {
    try {
      const q = query(
        collection(db, 'tickets'),
        where('status', '==', 'open')
      );

      return onSnapshot(q, (querySnapshot) => {
        const tickets = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        // Sort tickets client-side for now
        tickets.sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
          const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
          return dateB - dateA; // Descending order
        });
        callback(tickets);
      }, (error) => {
        console.error('Error in ticket subscription for open tickets:', error);
        callback([]);
      });
    } catch (error) {
      console.error('Error setting up open tickets subscription:', error);
      callback([]);
      return () => {};
    }
  },

  // Subscribe to tickets open to bid for service providers
  subscribeToOpenBidTickets(callback) {
    try {
      const q = query(
        collection(db, 'tickets'),
        where('isOpenToBid', '==', true)
      );

      return onSnapshot(q, (querySnapshot) => {
        const tickets = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        // Sort tickets client-side for now
        tickets.sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
          const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
          return dateB - dateA; // Descending order
        });
        callback(tickets);
      }, (error) => {
        console.error('Error in ticket subscription for open bid tickets:', error);
        callback([]);
      });
    } catch (error) {
      console.error('Error setting up open bid tickets subscription:', error);
      callback([]);
      return () => {};
    }
  }
}; 